class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}


// giftbox js

// document.addEventListener('DOMContentLoaded', function () {
//   const freeGiftVariantId = 50835170689322;
//   const minCartAmountCents = 100000; // HK$1000
//   const debounceMs = 400;

//   let isProcessing = false;
//   let lastState = { subtotal: null, giftInCart: null };
//   let debounceTimer = null;

//   async function getCart() {
//     const res = await fetch('/cart.js', { credentials: 'same-origin' });
//     if (!res.ok) throw new Error('Failed to fetch cart');
//     return res.json();
//   }

//   async function addFreeGift() {
//     await fetch('/cart/add.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       credentials: 'same-origin',
//       body: JSON.stringify({ id: Number(freeGiftVariantId), quantity: 1 })
//     });
//     await wait(200);
//     return getCart();
//   }

//   async function removeFreeGiftByLine(lineNumber) {
//     await fetch('/cart/change.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       credentials: 'same-origin',
//       body: JSON.stringify({ line: lineNumber, quantity: 0 })
//     });
//     await wait(200);
//     return getCart();
//   }

//   function wait(ms = 200) {
//     return new Promise(resolve => setTimeout(resolve, ms));
//   }

//   function findGiftItem(cart) {
//     if (!cart || !Array.isArray(cart.items)) return { found: false };
//     for (let i = 0; i < cart.items.length; i++) {
//       const it = cart.items[i];
//       const variantId = Number(it.id || it.variant_id);
//       if (variantId === Number(freeGiftVariantId)) {
//         return { found: true, index: i, item: it };
//       }
//     }
//     return { found: false };
//   }

//   // Always force free gift qty = 1
//   async function fixGiftQuantity(cart) {
//     const gift = findGiftItem(cart);

//     if (gift.found && gift.item.quantity > 1) {
//       await fetch('/cart/change.js', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         credentials: 'same-origin',
//         body: JSON.stringify({
//           line: gift.index + 1,
//           quantity: 1
//         })
//       });

//       await wait(200);
//       return getCart();
//     }

//     return cart;
//   }

//   // Refresh Shopify cart drawer + bubble
//   async function refreshCartUI(cart) {
//     try {
//       const res = await fetch(
//         window.location.pathname + "?sections=cart-drawer,cart-icon-bubble"
//       );

//       if (res.ok) {
//         const sections = await res.json();

//         const drawer = document.querySelector("#CartDrawer");
//         if (drawer && sections["cart-drawer"]) {
//           drawer.innerHTML = sections["cart-drawer"];
//         }

//         const bubble = document.querySelector("cart-icon-bubble, .cart-count-bubble, .cart-count");
//         if (bubble && sections["cart-icon-bubble"]) {
//           bubble.innerHTML = sections["cart-icon-bubble"];
//         }
//         return;
//       }
//     } catch (err) {
//       console.warn("Section refresh failed, fallback used.");
//     }

//     if (window.cartDrawer?.render) {
//       try { await window.cartDrawer.render(); return; } catch(e){}
//     }

//     try {
//       document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart || {} }));
//     } catch (e) {}

//     try {
//       if (cart.item_count != null) {
//         document.querySelectorAll('[data-cart-count], .cart-count')
//           .forEach(el => el.textContent = cart.item_count);
//       }
//     } catch (e) {}
//   }

//   async function checkCartAndSync() {
//     if (isProcessing) return;
//     isProcessing = true;

//     try {
//       let cart = await getCart();

//       // Correct free gift qty
//       cart = await fixGiftQuantity(cart);

//       const subtotal = cart.items_subtotal_price;
//       const foundObj = findGiftItem(cart);
//       const found = foundObj.found;

//       // ⭐ Always refresh UI but DO NOT early-return
//       await refreshCartUI(cart);
//       // console.log(subtotal);
//       // ⭐ Add free gift
//       if (subtotal >= minCartAmountCents && !found) {
//         let newCart = await addFreeGift();
//         newCart = await fixGiftQuantity(newCart);

//         const newFound = findGiftItem(newCart).found;
//         lastState = { subtotal: newCart.items_subtotal_price, giftInCart: newFound };

//         await refreshCartUI(newCart);
//         isProcessing = false;
//         return;
//       }

//       // ⭐ Remove free gift — FIXED LOGIC
//       if (subtotal < minCartAmountCents && found) {
//         const lineNumber = foundObj.index + 1;
//         let newCart = await removeFreeGiftByLine(lineNumber);
//         newCart = await fixGiftQuantity(newCart);

//         const newFound = findGiftItem(newCart).found;
//         lastState = { subtotal: newCart.items_subtotal_price, giftInCart: newFound };

//         await refreshCartUI(newCart);
//         isProcessing = false;
//         return;
//       }

//       lastState = { subtotal, giftInCart: found };

//     } catch (err) {
//       console.error('free-gift-sync error:', err);
//     } finally {
//       await wait(debounceMs);
//       isProcessing = false;
//     }
//   }

//   function scheduleCheck(delay = 500) {
//     if (debounceTimer) clearTimeout(debounceTimer);
//     debounceTimer = setTimeout(() => {
//       checkCartAndSync();
//       debounceTimer = null;
//     }, delay);
//   }

//   // Trigger sync on cart actions
//   document.addEventListener('submit', (e) => {
//     const form = e.target.closest('form[action^="/cart/add"]');
//     if (form) scheduleCheck(400);
//   }, true);

//   document.addEventListener('click', (e) => {
//     const t = e.target;
//     if (
//       t.matches('.cart__remove, .cart-remove-button, .cart__remove-button, .cart__remove-link') ||
//       t.matches('.quantity__button') ||
//       t.matches('[data-quantity-plus], [data-quantity-minus]') ||
//       t.closest('.cart__item')
//     ) {
//       scheduleCheck(300);
//     }
//   }, true);

//   document.addEventListener('change', (e) => {
//     if (e.target.matches('input.quantity__input, input.qty, input[name*="quantity"], select[name*="quantity"]')) {
//       scheduleCheck(300);
//     }
//   }, true);

//   ['cart:updated','cart:update','ajaxCart.afterUpdate','cart.update']
//     .forEach(ev => document.addEventListener(ev, () => scheduleCheck(300)));

//   // Observe drawer
//   (function observeCartElements() {
//     const selectors = ['cart-drawer', '.cart-drawer', '.cart__content', '.mini-cart', '#CartDrawer'];
//     for (const sel of selectors) {
//       const el = document.querySelector(sel);
//       if (el) {
//         const mo = new MutationObserver(() => scheduleCheck(300));
//         mo.observe(el, { childList: true, subtree: true, attributes: true });
//         break;
//       }
//     }
//   }());

//   // Run once at page load
//   scheduleCheck(200);

//   // Debug tool
//   window._freeGiftSync = {
//     checkNow: () => scheduleCheck(0),
//     lastState: () => lastState
//   };
// });

// document.addEventListener('DOMContentLoaded', function () {
//   const freeGiftVariantId = 50835170689322;
//   const minCartAmountCents = 100000; // HK$1000
//   const debounceMs = 400;

//   let isProcessing = false;
//   let lastState = { subtotal: null, giftInCart: null };
//   let debounceTimer = null;

//   async function getCart() {
//     const res = await fetch('/cart.js', { credentials: 'same-origin' });
//     if (!res.ok) throw new Error('Failed to fetch cart');
//     return res.json();
//   }

//   async function addFreeGift() {
//     await fetch('/cart/add.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       credentials: 'same-origin',
//       body: JSON.stringify({ id: Number(freeGiftVariantId), quantity: 1 })
//     });
//     await wait(200);
//     return getCart();
//   }

//   async function removeFreeGiftByLine(lineNumber) {
//     await fetch('/cart/change.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       credentials: 'same-origin',
//       body: JSON.stringify({ line: lineNumber, quantity: 0 })
//     });
//     await wait(200);
//     return getCart();
//   }

//   function wait(ms = 200) {
//     return new Promise(resolve => setTimeout(resolve, ms));
//   }

//   function findGiftItem(cart) {
//     if (!cart || !Array.isArray(cart.items)) return { found: false };
//     for (let i = 0; i < cart.items.length; i++) {
//       const it = cart.items[i];
//       const variantId = Number(it.id || it.variant_id);
//       if (variantId === Number(freeGiftVariantId)) {
//         return { found: true, index: i, item: it };
//       }
//     }
//     return { found: false };
//   }

//   async function fixGiftQuantity(cart) {
//     const gift = findGiftItem(cart);
//     if (gift.found && gift.item.quantity > 1) {
//       await fetch('/cart/change.js', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         credentials: 'same-origin',
//         body: JSON.stringify({ line: gift.index + 1, quantity: 1 })
//       });
//       await wait(200);
//       return getCart();
//     }
//     return cart;
//   }

//   async function refreshCartUI(cart) {
//     try {
//       const res = await fetch(
//         window.location.pathname + "?sections=cart-drawer,cart-icon-bubble"
//       );
//       if (res.ok) {
//         const sections = await res.json();

//         const drawer = document.querySelector("#CartDrawer");
//         if (drawer && sections["cart-drawer"]) {
//           drawer.innerHTML = sections["cart-drawer"];
//         }

//         const bubble = document.querySelector(".cart-count-bubble, .cart-icon-bubble");
//         if (bubble && sections["cart-icon-bubble"]) {
//           bubble.innerHTML = sections["cart-icon-bubble"];
//         }

//         return;
//       }
//     } catch (err) {
//       console.warn("Section refresh failed, fallback used.");
//     }

//     // Fallbacks
//     if (window.cartDrawer?.render) {
//       try { await window.cartDrawer.render(); return; } catch(e){}
//     }

//     try {
//       document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart || {} }));
//     } catch (e) {}
//   }

//   async function checkCartAndSync() {
//     if (isProcessing) return;
//     isProcessing = true;

//     try {
//       let cart = await getCart();

//       // Correct free gift qty
//       cart = await fixGiftQuantity(cart);

//       const subtotal = cart.items_subtotal_price;
//       const foundObj = findGiftItem(cart);
//       const found = foundObj.found;

//       // Refresh UI
//       await refreshCartUI(cart);

//       // Add free gift
//       if (subtotal >= minCartAmountCents && !found) {
//         let newCart = await addFreeGift();
//         newCart = await fixGiftQuantity(newCart);
//         lastState = { subtotal: newCart.items_subtotal_price, giftInCart: findGiftItem(newCart).found };
//         await refreshCartUI(newCart);
//         isProcessing = false;
//         return;
//       }

//       // Remove free gift
//       if (subtotal < minCartAmountCents && found) {
//         const lineNumber = foundObj.index + 1;
//         let newCart = await removeFreeGiftByLine(lineNumber);
//         newCart = await fixGiftQuantity(newCart);
//         lastState = { subtotal: newCart.items_subtotal_price, giftInCart: findGiftItem(newCart).found };
//         await refreshCartUI(newCart);
//         isProcessing = false;
//         return;
//       }

//       lastState = { subtotal, giftInCart: found };

//     } catch (err) {
//       console.error('free-gift-sync error:', err);
//     } finally {
//       await wait(debounceMs);
//       isProcessing = false;
//     }
//   }

//   function scheduleCheck(delay = 500) {
//     if (debounceTimer) clearTimeout(debounceTimer);
//     debounceTimer = setTimeout(() => {
//       checkCartAndSync();
//       debounceTimer = null;
//     }, delay);
//   }

//   // Trigger sync on cart actions
//   document.addEventListener('submit', (e) => {
//     const form = e.target.closest('form[action^="/cart/add"]');
//     if (form) scheduleCheck(400);
//   }, true);

//   document.addEventListener('click', (e) => {
//     const t = e.target;
//     if (
//       t.matches('.cart__remove, .cart-remove-button, .cart__remove-button, .cart__remove-link') ||
//       t.matches('.quantity__button') ||
//       t.matches('[data-quantity-plus], [data-quantity-minus]') ||
//       t.closest('.cart__item')
//     ) {
//       scheduleCheck(300);
//     }
//   }, true);

//   document.addEventListener('change', (e) => {
//     if (e.target.matches('input.quantity__input, input.qty, input[name*="quantity"], select[name*="quantity"]')) {
//       scheduleCheck(300);
//     }
//   }, true);

//   ['cart:updated','cart:update','ajaxCart.afterUpdate','cart.update']
//     .forEach(ev => document.addEventListener(ev, () => scheduleCheck(300)));

//   // Observe drawer for changes
//   (function observeCartElements() {
//     const selectors = ['cart-drawer', '.cart-drawer', '.cart__content', '.mini-cart', '#CartDrawer'];
//     for (const sel of selectors) {
//       const el = document.querySelector(sel);
//       if (el) {
//         const mo = new MutationObserver(() => scheduleCheck(300));
//         mo.observe(el, { childList: true, subtree: true, attributes: true });
//         break;
//       }
//     }
//   }());

//   // Run once at page load
//   scheduleCheck(200);

//   // Debug tool
//   window._freeGiftSync = {
//     checkNow: () => scheduleCheck(0),
//     lastState: () => lastState
//   };
// });

// document.addEventListener("DOMContentLoaded", function () {
//   const freeGiftVariantId = 50835170689322; // Replace with your actual variant ID
//   const minCartValue = 100000; // 1000 HKD in cents (Shopify uses cents)

//   function checkCartForGift() {
//     fetch("/cart.js")
//       .then(res => res.json())
//       .then(cart => {
//         const cartTotal = cart.total_price;
//         const giftItem = cart.items.find(item => item.variant_id === freeGiftVariantId);

//         if (cartTotal >= minCartValue && !giftItem) {
//           addGiftToCart();
//         } else if (cartTotal < minCartValue && giftItem) {
//           removeGiftFromCart(giftItem.key);
//         }
//       });
//   }

//   function addGiftToCart() {
//     fetch("/cart/add.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         items: [{
//           quantity: 1,
//           id: freeGiftVariantId,
//           properties: { is_gift: true }
//         }]
//       })
//     }).then(() => refreshCartUI());
//   }

//   function removeGiftFromCart(itemKey) {
//     fetch("/cart/change.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         id: itemKey,
//         quantity: 0
//       })
//     }).then(() => refreshCartUI());
//   }

//   function refreshCartUI() {
//     document.dispatchEvent(new CustomEvent('cart:refresh'));
//     // Optionally, trigger a drawer/cart bubble update for your theme...
//   }

//   // Listen for cart changes and check eligibility
//   document.addEventListener("cart:updated", checkCartForGift);
//   checkCartForGift();
// });
// giftbox js_updated_version
// document.addEventListener("DOMContentLoaded", function () {

//   const freeGiftVariantId = 50835170689322;

//   // --- Currency → HKD Conversion Table ---
//   // Replace with your REAL conversion values
//   const currencyToHKD = {
//     "HKD": 1,
//     "MOP": 0.97,  // Example: 1 MOP = 0.97 HKD
//     "TWD": 0.25,  // Example: 1 TWD = 0.25 HKD
//     "USD": 7.80   // Example: 1 USD = 7.8 HKD
//   };

//   // Minimum threshold
//   const HKD_THRESHOLD = 1000;


//   // Detect ALL cart changes
//   (function () {
//     const originalFetch = window.fetch;

//     window.fetch = function (resource, options) {
//       const isCartChange =
//         typeof resource === "string" &&
//         (resource.includes("/cart/add") ||
//          resource.includes("/cart/change"));

//       return originalFetch(resource, options).then(response => {
//         if (isCartChange) {
//           document.dispatchEvent(new Event("cart:updated"));
//         }
//         return response;
//       });
//     };
//   })();


//   // MAIN LOGIC
//   function checkCartForGift() {
//     fetch("/cart.js")
//       .then(res => res.json())
//       .then(cart => {

//         // Detect current store currency
//         const shopCurrency = cart.currency || "HKD";
//         const fx = currencyToHKD[shopCurrency] || 1;

//         // Convert cart total to HKD
//         const cartTotalHKD = (cart.items_subtotal_price / 100) * fx;

//         const giftItem = cart.items.find(item => item.variant_id === freeGiftVariantId);

//         // ADD gift
//         if (cartTotalHKD >= HKD_THRESHOLD && !giftItem) {
//           addGiftToCart().then(() => {
//             window.location.reload();
//           });
//         }

//         // REMOVE gift
//         if (cartTotalHKD < HKD_THRESHOLD && giftItem) {
//           removeGiftFromCart(giftItem.key).then(() => {
//             window.location.reload();
//           });
//         }
//       });
//   }


//   // Add gift
//   function addGiftToCart() {
//     return fetch("/cart/add.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         items: [
//           { quantity: 1, id: freeGiftVariantId }
//         ]
//       })
//     });
//   }

//   // Remove gift
//   function removeGiftFromCart(itemKey) {
//     return fetch("/cart/change.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ id: itemKey, quantity: 0 })
//     });
//   }

//   // After any user cart change
//   document.addEventListener("cart:updated", checkCartForGift);

//   // Initial load
//   checkCartForGift();
// });


// blocked_e_gift
document.addEventListener("DOMContentLoaded", function () {

  const freeGiftVariantId = 50835170689322;

  // ❗ Block gift if cart contains ANY variant of this product
  const blockedProductId = 9958139068714; // ← REPLACE with PRODUCT ID

  const currencyToHKD = {
    "HKD": 1,
    "MOP": 0.97,
    "TWD": 0.25,
    "USD": 7.80
  };

  const HKD_THRESHOLD = 1000;


  (function () {
    const originalFetch = window.fetch;
    window.fetch = function (resource, options) {
      const isCartChange =
        typeof resource === "string" &&
        (resource.includes("/cart/add") ||
         resource.includes("/cart/change"));
      return originalFetch(resource, options).then(response => {
        if (isCartChange) document.dispatchEvent(new Event("cart:updated"));
        return response;
      });
    };
  })();


  function checkCartForGift() {
    fetch("/cart.js")
      .then(res => res.json())
      .then(cart => {

        const shopCurrency = cart.currency || "HKD";
        const fx = currencyToHKD[shopCurrency] || 1;

        const cartTotalHKD = (cart.items_subtotal_price / 100) * fx;

        const hasGift = cart.items.some(item => item.variant_id === freeGiftVariantId);

        // 🔍 Check if cart contains ANY VARIANT of the blocked product
        const hasBlockedProduct = cart.items.some(item => item.product_id === blockedProductId);

        // ⛔ Block gift entirely if blocked product is present
        if (hasBlockedProduct) {
          if (hasGift) {
            removeGiftFromCart(cart.items.find(i => i.variant_id === freeGiftVariantId).key)
              .then(() => window.location.reload());
          }
          return;
        }

        // ➕ Add gift
        if (cartTotalHKD >= HKD_THRESHOLD && !hasGift) {
          addGiftToCart().then(() => window.location.reload());
        }

        // ➖ Remove gift
        if (cartTotalHKD < HKD_THRESHOLD && hasGift) {
          removeGiftFromCart(cart.items.find(i => i.variant_id === freeGiftVariantId).key)
            .then(() => window.location.reload());
        }

      });
  }


  function addGiftToCart() {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ quantity: 1, id: freeGiftVariantId }] })
    });
  }

  function removeGiftFromCart(itemKey) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: itemKey, quantity: 0 })
    });
  }

  document.addEventListener("cart:updated", checkCartForGift);
  checkCartForGift();
});





